import { useEffect, useMemo, useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import "../../styles/Header.css";
import { FaUserCircle, FaShoppingCart, FaSearch, FaUserShield } from "react-icons/fa";
import logoAlfasol from "../../assets/logo.png";
import { useCart } from "../../context/CartContext";

export default function Header() {
  const { t, i18n } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);

  const navigate = useNavigate();
  const { authenticated, user } = useAuth();
  const { totalItems } = useCart();

  const initial =
    user?.name?.charAt(0).toUpperCase() ||
    user?.email?.charAt(0).toUpperCase();

  useEffect(() => {
    const handleScroll = () => setMenuOpen(false);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    const response = await fetch("/api/products", {
      credentials: "include",
    });

    if (response.ok) {
      setProducts(await response.json());
    }
  };

  const searchResults = useMemo(() => {
    const value = search.trim().toLowerCase();

    if (value.length < 2) {
      return [];
    }

    return products
      .filter((product) =>
        `${product.name ?? ""} ${product.brand ?? ""} ${product.sku ?? ""}`
          .toLowerCase()
          .includes(value)
      )
      .slice(0, 6);
  }, [products, search]);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const toggleMenu = () => {
    setMenuOpen((prev) => !prev);
  };

  const handleNavigation = () => {
    setMenuOpen(false);
    setSearch("");
    setSearchOpen(false);
  };

  const goToProduct = (slug) => {
    handleNavigation();
    navigate(`/shop/${slug}`);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();

    if (searchResults.length > 0) {
      goToProduct(searchResults[0].slug);
      return;
    }

    if (search.trim()) {
      handleNavigation();
      navigate("/shop", { state: { search: search.trim() } });
    }
  };

  const isAdmin = user?.roles?.includes("ROLE_ADMIN");

  return (
    <header className="header">
      <div className="container header-container">
        <div className="logo">
          <Link to="/" onClick={handleNavigation}>
            <img src={logoAlfasol} alt="Alfasol" />
          </Link>
        </div>

        <button
          type="button"
          className="menu-toggle"
          onClick={toggleMenu}
          aria-label="Abrir menú"
        >
          ☰
        </button>

        <nav className={menuOpen ? "open" : ""}>
          <ul>
            <li>
              <NavLink to="/" end onClick={handleNavigation}>
                {t("nav.home")}
              </NavLink>
            </li>

            <li>
              <NavLink to="/about" onClick={handleNavigation}>
                {t("nav.about")}
              </NavLink>
            </li>

            <li>
              <NavLink to="/portfolio" onClick={handleNavigation}>
                {t("nav.portfolio")}
              </NavLink>
            </li>

            <li>
              <NavLink to="/shop" onClick={handleNavigation}>
                {t("nav.shop")}
              </NavLink>
            </li>

            <li>
              <NavLink to="/blog" onClick={handleNavigation}>
                {t("nav.blog")}
              </NavLink>
            </li>

            <li>
              <NavLink to="/contact" onClick={handleNavigation}>
                {t("nav.contact")}
              </NavLink>
            </li>
          </ul>
        </nav>

        <div className="header-search">
          <form onSubmit={handleSearchSubmit}>
            <FaSearch className="header-search-icon" />

            <input
              type="text"
              value={search}
              placeholder={t("header.searchPlaceholder")}
              onFocus={() => setSearchOpen(true)}
              onChange={(e) => {
                setSearch(e.target.value);
                setSearchOpen(true);
              }}
            />
          </form>

          {searchOpen && searchResults.length > 0 && (
            <div className="header-search-results">
              {searchResults.map((product) => (
                <button
                  key={product.id}
                  type="button"
                  onClick={() => goToProduct(product.slug)}
                >
                  {product.imageUrl && (
                    <img src={product.imageUrl} alt={product.name} />
                  )}

                  <span>
                    <strong>{product.name}</strong>
                    <small>
                      {product.brand || product.sku || ""} ·{" "}
                      {Number(product.price).toFixed(2)} €
                    </small>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="header-actions">
          <select
            className="language-select"
            value={i18n.language}
            onChange={(e) => changeLanguage(e.target.value)}
            aria-label="Idioma"
          >
            <option value="es">ES</option>
            <option value="en">EN</option>
          </select>

          {isAdmin && (
            <button
              className="admin-icon-button"
              onClick={() => {
                handleNavigation();
                navigate("/admin");
              }}
              title="Admin"
            >
              <FaUserShield />
            </button>
          )}

          <button
            className="cart-button"
            onClick={() => {
              handleNavigation();
              navigate("/cart");
            }}
            title={t("cart.title")}
          >
            <FaShoppingCart />

            {totalItems > 0 && <span className="cart-count">{totalItems}</span>}
          </button>

          {authenticated ? (
            <button
              className="auth-icon"
              onClick={() => {
                handleNavigation();
                navigate("/profile");
              }}
              title={user?.name || user?.email || t("header.profile")}
            >
              {initial}
            </button>
          ) : (
            <button
              className="auth-icon"
              onClick={() => {
                handleNavigation();
                navigate("/login");
              }}
              title={t("login.title")}
            >
              <FaUserCircle />
            </button>
          )}
        </div>
      </div>
    </header>
  );
}